import React, { useState } from 'react';

import cn from '@/utils/cn';

import { toneText } from './tone';
import { FrameKind, Spread, SpreadFrame, SpreadImage } from './types';

// SVG fills/strokes (Tailwind classes can't paint SVG) — tints of the project palette on the
// white "paper" of the drawn pages, so they stay the same in either theme; textClass is the
// matching SPA text token for HTML labels.
interface KindMeta {
  fill: string;
  label: string;
  note: string;
  stroke: string;
  textClass: string;
}

const KIND_META: Record<FrameKind, KindMeta> = {
  caption: {
    fill: 'rgba(122, 91, 191, 0.18)',
    label: 'Caption',
    note: 'Paired with its nearest image and compared against the online caption.',
    stroke: '#7a5bbf',
    textClass: toneText.accent,
  },
  fragment: {
    fill: 'rgba(154, 148, 168, 0.14)',
    label: 'Small fragment',
    note: 'A few words (label, prompt) that map to nothing — counted, not compared.',
    stroke: '#9a94a8',
    textClass: toneText.neutral,
  },
  furniture: {
    fill: 'rgba(154, 148, 168, 0.18)',
    label: 'Page furniture',
    note: 'Running heads, footers, title labels — ignored on purpose.',
    stroke: '#9a94a8',
    textClass: toneText.neutral,
  },
  matched: {
    fill: 'rgba(46, 125, 67, 0.16)',
    label: 'Reading section (mapped)',
    note: 'Compared word-for-word against its online section.',
    stroke: '#2e7d43',
    textClass: toneText.success,
  },
  unclaimed: {
    fill: 'rgba(179, 56, 44, 0.14)',
    label: 'Doesn’t map to anything online',
    note: 'New print content, or a title mismatch. If it should pour, title it to match an online section.',
    stroke: '#b3382c',
    textClass: toneText.error,
  },
  vocab: {
    fill: 'rgba(82, 46, 145, 0.14)',
    label: 'Vocabulary card',
    note: 'A lesson-opener vocabulary term — feeds the Vocabulary box.',
    stroke: '#522e91',
    textClass: toneText.accent,
  },
};

const WARN_STROKE = '#b45309';
const IMAGE_FILL = 'rgba(91, 143, 168, 0.15)';
const IMAGE_STROKE = '#5b8fa8';
const PAD = 12;

const frameKey = (frame: SpreadFrame) => `${frame.storyId}-${frame.x}-${frame.y}`;

// Approximates InDesign text wrap for the common layout: a text frame spans the full column but
// an image sits on its top, pushing the visible text below. Text starts under any image that
// covers most of the frame's width at its current top.
const textTop = (frame: SpreadFrame, images: SpreadImage[]) => {
  let top = frame.y;
  [...images].sort((a, b) => a.y - b.y).forEach((image) => {
    const overlap = Math.min(frame.x + frame.width, image.x + image.width) - Math.max(frame.x, image.x);
    const coversTop = image.y <= top + 2 && image.y + image.height > top;
    if (overlap >= frame.width * 0.6 && coversTop) top = image.y + image.height;
  });
  return top;
};

interface FrameTextProps {
  fontSize: number;
  frame: SpreadFrame;
  images: SpreadImage[];
}

// Greeked story text inside the frame, title bolded. foreignObject sizes are SVG user units
// (they scale with the spread), so the font is sized inline rather than with Tailwind's
// px-based classes.
const FrameText = ({ fontSize, frame, images }: FrameTextProps) => {
  const top = textTop(frame, images);
  const height = frame.y + frame.height - top - 6;
  if (height < fontSize * 1.3 || frame.width < 24) return null;

  // The story text begins with its title paragraph unless the title was truncated.
  const titled = frame.text.startsWith(frame.title);

  return (
    <foreignObject
      height={height}
      pointerEvents="none"
      width={frame.width - 10}
      x={frame.x + 5}
      y={top + 3}
    >
      <div style={{ color: '#3c3c3c', fontSize, lineHeight: 1.35, maxHeight: '100%', overflow: 'hidden' }}>
        {titled
          ? <><strong>{frame.title}</strong> {frame.text.slice(frame.title.length).trim()}</>
          : frame.text}
      </div>
    </foreignObject>
  );
};

const viewBox = (spread: Spread) => {
  const boxes = [...spread.pages, ...spread.frames, ...spread.images];
  if (boxes.length === 0) return { height: 100, width: 100, x: 0, y: 0 };

  const minX = Math.min(...boxes.map(b => b.x)) - PAD;
  const minY = Math.min(...boxes.map(b => b.y)) - PAD;
  const maxX = Math.max(...boxes.map(b => b.x + b.width)) + PAD;
  const maxY = Math.max(...boxes.map(b => b.y + b.height)) + PAD + 24;
  return { height: maxY - minY, width: maxX - minX, x: minX, y: minY };
};

// Sized against the WIDEST spread: every spread renders at the same units-per-pixel (see the
// svg width below), so one shared unit size gives the same on-screen type everywhere.
const fontFor = (width: number) => Math.max(9, Math.round(width / 72));

const pagesLabel = (spread: Spread) => spread.pages.map(page => page.name).filter(Boolean).join('–') || '?';

interface FrameDetailProps {
  frame: SpreadFrame | null;
}

const FrameDetail = ({ frame }: FrameDetailProps) => {
  if (!frame) {
    return (
      <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">
        Click a frame to see what the tool made of it.
      </p>
    );
  }

  const meta = KIND_META[frame.kind];
  return (
    <>
      <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-spa-heading">“{frame.title}”</p>
      <p className={cn('tw-m-0 tw-mt-1 tw-text-sm tw-font-bold', meta.textClass)}>{meta.label}</p>
      <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-spa-secondary">
        {frame.words} word{frame.words === 1 ? '' : 's'}
        {frame.sectionTitle ? ` · compared with “${frame.sectionTitle}”` : ''}
      </p>
      <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-spa-secondary">{meta.note}</p>
      {frame.styleWarning && (
        <p className={cn('tw-m-0 tw-mt-1 tw-text-xs tw-font-bold', toneText.warning)}>
          Contains paragraphs with an unrecognized style — see Style coverage below.
        </p>
      )}
    </>
  );
};

// The print file drawn the way the author sees it: each spread as a mini pasteboard, every text
// frame color-coded by what the tool decided it is. Made for showing layout folks exactly which
// box on which page needs attention.
const SpreadMap = ({ spreads }: { spreads: Spread[] }) => {
  const [index, setIndex] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  if (spreads.length === 0) return null;

  const spread = spreads[Math.min(index, spreads.length - 1)];
  const selected = spread.frames.find(frame => frameKey(frame) === selectedKey) || null;
  const box = viewBox(spread);
  // Render every spread at the same scale: a single page takes proportionally less width than
  // the widest (double) spread instead of stretching to fill the container.
  const maxWidth = Math.max(...spreads.map(s => viewBox(s).width));
  const fontSize = fontFor(maxWidth);

  const selectSpread = (i: number) => {
    setIndex(i);
    setSelectedKey(null);
  };

  return (
    <div className="tw-mt-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
        {spreads.map((s, i) => (
          <button
            key={pagesLabel(s) + String(i)}
            className={cn(
              'tw-box-border tw-cursor-pointer tw-rounded tw-border-0 tw-px-2 tw-py-1 tw-text-xs tw-font-bold',
              i === index ? 'tw-bg-spa-accent-solid tw-text-white' : 'tw-bg-spa-bg-tertiary tw-text-spa-secondary',
            )}
            type="button"
            onClick={() => selectSpread(i)}
          >
            {pagesLabel(s)}
          </button>
        ))}
        <span className="tw-ml-2 tw-text-xs tw-text-spa-secondary">Pages</span>
      </div>

      <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-4">
        <div className="tw-min-w-0 tw-flex-1">
          <svg
            aria-label={`Spread with pages ${pagesLabel(spread)}, frames color-coded by detection result`}
            className="tw-rounded tw-border tw-border-solid tw-border-spa-border tw-bg-spa-bg-tertiary"
            role="img"
            style={{ width: `${(box.width / maxWidth) * 100}%` }}
            viewBox={`${box.x} ${box.y} ${box.width} ${box.height}`}
          >
            {spread.pages.map(page => (
              <React.Fragment key={page.name + String(page.x)}>
                <rect fill="#ffffff" height={page.height} stroke="#d4d0dd" width={page.width} x={page.x} y={page.y} />
                <text
                  fill="#948fa0"
                  fontSize={fontSize * 1.2}
                  textAnchor="middle"
                  x={page.x + page.width / 2}
                  y={page.y + page.height + fontSize * 1.6}
                >
                  {page.name}
                </text>
              </React.Fragment>
            ))}
            {spread.images.map(image => (
              <React.Fragment key={`${image.asset}-${image.x}-${image.y}`}>
                <rect
                  fill={IMAGE_FILL}
                  height={image.height}
                  stroke={IMAGE_STROKE}
                  strokeWidth="1.5"
                  width={image.width}
                  x={image.x}
                  y={image.y}
                >
                  <title>{image.asset || 'Pasted image (no file link)'}</title>
                </rect>
                {/* The classic image-placeholder X, plus the asset filename — the useful part. */}
                <path
                  d={`M${image.x} ${image.y} l${image.width} ${image.height} `
                  + `M${image.x + image.width} ${image.y} l${-image.width} ${image.height}`}
                  fill="none"
                  stroke={IMAGE_STROKE}
                  strokeWidth="0.75"
                />
                <foreignObject
                  height={fontSize * 1.5}
                  pointerEvents="none"
                  width={image.width - 8}
                  x={image.x + 4}
                  y={image.y + 2}
                >
                  <div
                    style={{
                      color: '#3f6478',
                      fontFamily: 'monospace',
                      fontSize: fontSize * 0.9,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {image.asset || 'pasted image — no file link'}
                  </div>
                </foreignObject>
              </React.Fragment>
            ))}
            {spread.frames.map(frame => (
              <React.Fragment key={frameKey(frame)}>
                <rect
                  aria-label={`${frame.title} — ${KIND_META[frame.kind].label}`}
                  className="tw-cursor-pointer"
                  fill={KIND_META[frame.kind].fill}
                  height={frame.height}
                  role="button"
                  stroke={frame.styleWarning ? WARN_STROKE : KIND_META[frame.kind].stroke}
                  strokeDasharray={frame.styleWarning ? '6 4' : undefined}
                  strokeWidth={frameKey(frame) === selectedKey ? 4 : 1.5}
                  tabIndex={0}
                  width={frame.width}
                  x={frame.x}
                  y={frame.y}
                  onClick={() => setSelectedKey(frameKey(frame))}
                  onKeyDown={e => e.key === 'Enter' && setSelectedKey(frameKey(frame))}
                >
                  <title>{`${frame.title} — ${KIND_META[frame.kind].label}`}</title>
                </rect>
                <FrameText fontSize={fontSize} frame={frame} images={spread.images} />
              </React.Fragment>
            ))}
          </svg>
        </div>

        <div className="tw-box-border tw-w-64 tw-rounded tw-border tw-border-solid tw-border-spa-border tw-p-3">
          <FrameDetail frame={selected} />
        </div>
      </div>

      <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1">
        {Object.values(KIND_META).map(meta => (
          <span key={meta.label} className="tw-flex tw-items-center tw-gap-1">
            <span className="tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-sm" style={{ background: meta.stroke }} />
            <span className="tw-text-xs tw-text-spa-secondary">{meta.label}</span>
          </span>
        ))}
        <span className="tw-flex tw-items-center tw-gap-1">
          <span className="tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-sm" style={{ background: IMAGE_STROKE }} />
          <span className="tw-text-xs tw-text-spa-secondary">Image</span>
        </span>
        <span className="tw-flex tw-items-center tw-gap-1">
          <span
            className="tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-sm tw-border tw-border-dashed"
            style={{ borderColor: WARN_STROKE }}
          />
          <span className="tw-text-xs tw-text-spa-secondary">Style problem</span>
        </span>
      </div>
    </div>
  );
};

export default SpreadMap;
